import { useCallback, useEffect, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { getContainer } from '@framework/di/getContainer';
import ContactRepository from '@/creditflowresearch/infrastructure/repository/ContactRepository';
import { useSessionContext } from '@users/session/SessionContext';
import { IUserPlanState } from '@/creditflowresearch/application/User/UserPlan/types';
import { initUserPlan } from '@/creditflowresearch/application/User/UserPlan/initialState';

export const useFetchUserPlan = (
  currentPlan: IUserPlanState,
  setCurrentPlan: React.Dispatch<React.SetStateAction<IUserPlanState>>,
  initial: IUserPlanState,
) => {
  const container = getContainer();
  const contactRepository = container.get<ContactRepository>(ContactRepository);
  const { currentUser } = useSessionContext();
  const hasFetched = useRef(false);

  const getUserPlan = useCallback(async () => {
    try {
      const userPlan = await contactRepository.getUserCurrentPlan();
      setCurrentPlan((prevState) => ({ ...prevState, ...userPlan }));
    } catch (error) {
      setCurrentPlan(initUserPlan);
    }
  }, [contactRepository, setCurrentPlan]);

  useEffect(() => {
    if (!isEqual(currentPlan, initial)) {
      setCurrentPlan(initial);
    }
  }, [initial]);

  useEffect(() => {
    if (!currentUser?.id) {
      return;
    }

    const shouldFetch = !currentPlan || isEmpty(currentPlan) || currentPlan?.hasServerError || !hasFetched.current;

    if (shouldFetch) {
      hasFetched.current = true;
      getUserPlan();
    }
  }, [currentUser?.id, currentPlan, getUserPlan]);

  return {
    getUserPlan,
  };
};
